import * as React from 'react';
import PlacesAutocomplete, { geocodeByPlaceId } from 'react-places-autocomplete';
import { PortalFieldLabel } from 'components/FormControls';
import { IMeta } from 'models/common';
import { IGeoLocation } from 'models/map';
import renderIcon, { IconName } from 'helpers/renderIcon';
import { addURLSearchParams, deleteURLSearchParams, readURLSearchParams } from 'helpers/common';
import { withRouter } from 'react-router';
import { IStore } from 'redux/IStore';
const classNames = require('classnames');
const { connect } = require('react-redux');
import { requestStart } from 'redux/modules/shop/offices';
const LOCATIONAUTOCOMPLETE_USAGE = {
	portal: '',
	admin: 'locationAutocompleteAdmin',
};
interface IProps {
	className?: string;
	usage?: 'portal' | 'admin';
	input: any;
	meta?: IMeta;
	label?: string;
	description?: string;
	information?: string;
	placeholder?: string;
	includeDetails?: boolean;
	onGetGeocodeByAddressError?: (error) => void;
	customSearchTypes?: string[];
	iconName?: IconName;
	iconClassName?: string;
	iconAction?: () => void | undefined;
	customIconSizeWidth?: number;
	customIconSizeHeight?: number;
	highlightWhenValueSet?: boolean;
	putSelectedValueInUrlQuery?: string; // url prop key name
	addLatToUrl?: string; // url prop key name
	addLngToUrl?: string; // url prop key name
	showPlaceholderWhileSearching?: () => void;
	location?: any; // obtained with withRouter
	limitCountryCodes?: string;
}
interface IState {
	location: string;
}
@connect(
	(state: IStore) => ({
		geoLatitude: state.offices?.geoCoordinates?.latitude,
		geoLongidute: state.offices?.geoCoordinates?.longitude,
	}),
	(dispatch) => ({
		requestStart: () => dispatch(requestStart()),
	}),
)
class PortalLocationAutocompleteW extends React.Component<IProps, IState> {
	private firstResultRef;
	constructor(props) {
		super(props);
		if (props.input.value) {
			this.state = {
				location: props.input.value,
			};
		}
		this.handleClearSelectedValue = this.handleClearSelectedValue.bind(this);
		this.firstResultRef = React.createRef();
	}
	private getAddress(value: string | IGeoLocation): string {
		if (typeof value === 'string') {
			return value;
		}
		if (typeof value === 'object' && value && 'formatted_address' in value) {
			return value.formatted_address;
		}
		return '';
	}
	//////////////////////////////////////////////
	// Apply values from URL query on mount - if any
	//////////////////////////////////////////////
	public componentDidMount(): void {
		if (this.props.putSelectedValueInUrlQuery) {
			const extractedParamValue = readURLSearchParams(this.props.putSelectedValueInUrlQuery);
			if (extractedParamValue) {
				this.setState({ location: extractedParamValue });
			}
		}
	}
	//////////////////////////////////////////////
	public componentDidUpdate(prevProps: IProps) {
		const inputValue = this.getAddress(this.props.input.value);
		const prevValue = this.getAddress(prevProps.input.value);
		if (!prevValue && inputValue) {
			this.setState({
				location: inputValue,
			});
		}
		if (!inputValue && prevValue) {
			this.setState({
				location: inputValue,
			});
		}
		if (
			prevProps.location !== this.props.location &&
			this.props.putSelectedValueInUrlQuery &&
			!readURLSearchParams(this.props.putSelectedValueInUrlQuery)
		) {
			this.setState({ location: '' });
		}
	}
	public state = {
		location: '',
	};
	private handleChange = (address) => {
		this.setState({ location: address });
	};
	private handleOnSelect = (address: string, placeId: string) => {
		if (this.props.showPlaceholderWhileSearching) {
			this.props.showPlaceholderWhileSearching();
		}
		this.setState({ location: address });
		////////////////////////////////////////////////////
		// Add values as URL search params
		////////////////////////////////////////////////////
		if (this.props.putSelectedValueInUrlQuery && address) {
			addURLSearchParams(this.props.putSelectedValueInUrlQuery, address);
		} else if (this.props.putSelectedValueInUrlQuery && !address) {
			deleteURLSearchParams([this.props.putSelectedValueInUrlQuery]);
		}
		////////////////////////////////////////////////////
		if (this.props.includeDetails) {
			// this.props.input.onChange(details);
			geocodeByPlaceId(placeId)
				.then((results) => {
					// const result = results[0];
					if (results.length) {
						this.props.input.onChange({
							...results[0],
							fullLocation: address,
						});
						if (this.props.addLatToUrl && this.props.addLngToUrl && results[0] && results[0].geometry) {
							addURLSearchParams(this.props.addLatToUrl, String(results[0].geometry.location?.lat()));
							addURLSearchParams(this.props.addLngToUrl, String(results[0].geometry.location?.lng()));
						} else if (this.props.addLatToUrl && this.props.addLngToUrl && !results[0] && !results[0].geometry) {
							deleteURLSearchParams([this.props.addLatToUrl, this.props.addLngToUrl]);
						}
					}
				})
				.catch((error) => {
					if (this.props.onGetGeocodeByAddressError) {
						this.props.onGetGeocodeByAddressError(error);
					}
					if (this.props.addLatToUrl && this.props.addLngToUrl) {
						deleteURLSearchParams([this.props.addLatToUrl, this.props.addLngToUrl]);
					}
				});
		}
		if (!this.props.includeDetails && this.props.input.onChange) {
			this.props.input.onChange(address);
		}
		if (!placeId) this.firstResultRef.current?.click();
	};
	private handleClearSelectedValue(e: React.MouseEvent) {
		if (e) {
			e.stopPropagation();
			e.preventDefault();
		}
		this.setState({ location: '' });
		deleteURLSearchParams([this.props.putSelectedValueInUrlQuery, this.props.addLatToUrl, this.props.addLngToUrl]);
		this.props.input.onChange('');
	}
	public render() {
		const {
			meta,
			input,
			label,
			usage,
			placeholder,
			information,
			className,
			iconName,
			iconClassName,
			iconAction,
			customIconSizeHeight,
			customIconSizeWidth,
			customSearchTypes,
			highlightWhenValueSet,
			limitCountryCodes,
		} = this.props;
		let description = this.props.description;
		let status = '';
		if (meta?.touched) {
			if (meta?.warning) {
				status = 'withWarning';
				description = meta.warning;
			}
			if (meta?.error) {
				description = meta.error;
				status = 'withError';
			}
		}
		let informationContainer: JSX.Element = null;
		if (typeof information !== 'undefined' && information !== null) {
			informationContainer = <div className="informationBlock">{information}</div>;
		}
		const usageT = usage ? LOCATIONAUTOCOMPLETE_USAGE[usage] : LOCATIONAUTOCOMPLETE_USAGE.portal;
		const searchOptionsConfig = { types: customSearchTypes || undefined, componentRestrictions: undefined };
		if (limitCountryCodes) {
			searchOptionsConfig.componentRestrictions = { country: limitCountryCodes };
		}
		return (
			<div className={classNames(className, 'fieldGroup', usageT, status)}>
				{informationContainer}
				<PlacesAutocomplete
					searchOptions={searchOptionsConfig}
					onChange={this.handleChange}
					value={this.state.location}
					onSelect={this.handleOnSelect}
				>
					{({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
						const inputProps = getInputProps({
							placeholder: placeholder ? placeholder : '',
							className: 'inputField',
						});
						return (
							<>
								<label
									className={classNames('inputHolder cursor-text-hover', {
										/*value: inputProps.value,*/ highlight: highlightWhenValueSet && this.state.location,
									})}
								>
									<input
										{...inputProps}
										className={classNames('inputField', {
											// value: props.input?.value,
											hasIconRight: iconName,
											// withoutLabel: !props.label,
										})}
									/>
									<PortalFieldLabel name={input.name} text={label} tag="div" />
									{iconName
										? this.state.location
											? renderIcon(
													'cancel_search',
													customIconSizeWidth || undefined,
													customIconSizeHeight || undefined,
													'clearFieldIcon pointer',
													this.handleClearSelectedValue,
											  )
											: renderIcon(
													iconName,
													customIconSizeWidth || 22,
													customIconSizeHeight || 22,
													iconClassName,
													iconAction,
											  )
										: null}
								</label>
								{suggestions.length > 0 ? (
									<div className="autocomplete-dropdown-container">
										{loading && <div className="loading">Loading...</div>}
										{suggestions.map((suggestion, i) => {
											return (
												<div
													{...(i == 0 ? { ref: this.firstResultRef } : {})}
													{...getSuggestionItemProps(suggestion, {
														className: classNames('suggestion-item', { active: suggestion.active }),
													})}
													key={suggestion.placeId}
												>
													<span>{suggestion.description}</span>
												</div>
											);
										})}
									</div>
								) : null}
							</>
						);
					}}
				</PlacesAutocomplete>
				{description ? <span className="note">{description}</span> : null}
			</div>
		);
	}
}
export const PortalLocationAutocomplete = withRouter(PortalLocationAutocompleteW);
